/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Tab,
  Tabs,
  Table,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../hooks/hooks';
import ChangePasswordModal from '../components/settings/ChangePasswordModal';
import ChangeEmailModal from '../components/settings/ChangeEmailModal';
import { OrganisationEditModal } from '../components/settings/OrganisationEditModal';
import { supabase } from '../lib/supabase';
import { SubscriptionUpgradeModal } from '../components/settings/SubscriptionUpgradeModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { captureSupabaseError } from '../lib/sentry';
import { tracking } from '../util/tracking';
import { setUser } from '../features/auth/authSlice';
import {
  clearSelectedOrganisation,
  setSelectedOrganisation,
} from '../features/organisation/organisationSlice';
import type { Organisation, UserOrganisation } from '../types/types';

// Define the interface based on your SQL View
export interface ViewOrganisation {
  profile_id: string;
  role: string;
  status: string;
  organisation_id: string;
  organisation_name: string;
  plan_name: string | null;
  tier: string | null;
  features: any;
  billing_customer_id: string | null;
}

const MySettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const selectedOrganisation = useAppSelector(
    (state) => state.organisation.selected,
  ) as UserOrganisation | null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(true); // Separate loading for orgs
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [confirmedAccountDeletion, setConfirmedAccountDeletion] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organisations, setOrganisations] = useState<ViewOrganisation[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [selectedPlanUpgradeOrg, setSelectedPlanUpgradeOrg] =
    useState<ViewOrganisation | null>(null);
  const [showEditOrganisationModal, setShowEditOrganisationModal] =
    useState(false);
  const [selectedOrganisationToEdit, setSelectedOrganisationToEdit] =
    useState<ViewOrganisation | null>(null);

  const [changePassword, setChangePassword] = useState(false);
  const [changeEmail, setChangeEmail] = useState(false);
  const [firstName, setFirstName] = useState(user?.profile?.first_name || '');
  const [lastName, setLastName] = useState(user?.profile?.last_name || '');

  const subscriptionUpdated = searchParams.get('subscription_updated');
  const flow = searchParams.get('flow'); // e.g., 'upgrade' or 'signup'
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam === 'organisation' ? 'organisation' : 'account';

  useEffect(() => {
    if (tabParam === 'account' || tabParam === 'organisation') return;

    const nextParams = new URLSearchParams(window.location.search);
    nextParams.set('tab', 'account');
    setSearchParams(nextParams, { replace: true });
  }, [setSearchParams, tabParam]);

  const fetchOrganisations = useCallback(async () => {
    if (!user?.id) {
      setOrganisations([]);
      return;
    }

    try {
      setOrgsLoading(true);
      const { data, error: orgError } = await supabase
        .from('v_my_orgs')
        .select('*')
        .eq('profile_id', user.id);

      if (orgError) {
        throw orgError;
      }

      setOrganisations(data || []);
    } catch (err: any) {
      captureSupabaseError(err, { op: 'organisations.fetch' });
      console.error('Error fetching organisations:', err.message);
    } finally {
      setOrgsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void fetchOrganisations();
  }, [fetchOrganisations]);

  const onChange = (field: string, value: string) => {
    switch (field) {
      case 'first_name':
        setFirstName(value);
        break;
      case 'last_name':
        setLastName(value);
        break;
      default:
        break;
    }
  };

  const onSave = async () => {
    tracking.log('UI', 'settings_save', { page: 'my_settings' });
    if (!firstName || !lastName) {
      setError('First and last name are required');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user?.id);

      if (updateError) throw updateError;
    } catch (err: any) {
      captureSupabaseError(err, { op: 'profile.update' });
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getPlanDisplay = (org: ViewOrganisation) => {
    if (!org.plan_name || org.plan_name.toLowerCase() === 'free')
      return (
        <>
          Free{' '}
          <Button
            variant="link"
            className="p-0 ms-2"
            onClick={() => {
              tracking.log('UI', 'plan_upgrade_open', {
                organisationId: org.organisation_id,
                planName: org.plan_name ?? 'free',
              });
              setShowUpgradeModal(true);
              setSelectedPlanUpgradeOrg(org);
            }}
          >
            Upgrade
          </Button>
        </>
      );

    return (
      <>
        {org.plan_name}{' '}
        <Button
          variant="link"
          className="p-0 ms-2"
          onClick={() => {
            tracking.log('UI', 'plan_upgrade_open', {
              organisationId: org.organisation_id,
              planName: org.plan_name ?? 'unknown',
            });
            setShowUpgradeModal(true);
            setSelectedPlanUpgradeOrg(org);
          }}
        >
          Change plan
        </Button>
      </>
    );
  };

  const handleTabSelect = (tabKey: string | null) => {
    const nextTab = tabKey === 'organisation' ? 'organisation' : 'account';
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', nextTab);
    setSearchParams(nextParams);
  };

  const handleEditOrganisation = (organisation: ViewOrganisation) => {
    setSelectedOrganisationToEdit(organisation);
    setShowEditOrganisationModal(true);
  };

  const handleCloseEditOrganisationModal = () => {
    setShowEditOrganisationModal(false);
    setSelectedOrganisationToEdit(null);
  };

  const handleOrganisationSaved = async (updatedOrganisation: Organisation) => {
    await fetchOrganisations();

    if (
      selectedOrganisation?.organisation?.id === updatedOrganisation.id &&
      selectedOrganisation
    ) {
      dispatch(
        setSelectedOrganisation({
          ...selectedOrganisation,
          organisation: {
            ...selectedOrganisation.organisation,
            ...updatedOrganisation,
          },
        }),
      );
    }

    handleCloseEditOrganisationModal();
  };

  const handleDeleteAccount = async () => {
    setError(null);
    setDeletingAccount(true);

    try {
      const { data, error: deleteError } =
        await supabase.functions.invoke('delete-user');

      if (deleteError) {
        throw deleteError;
      }

      if (!data?.success && data?.status !== 200) {
        throw new Error('Failed to delete account');
      }

      const { error: signOutError } = await supabase.auth.signOut({
        scope: 'global',
      });

      if (
        signOutError &&
        signOutError.message !==
          'Session from session_id claim in JWT does not exist'
      ) {
        throw signOutError;
      }

      dispatch(setUser(null));
      dispatch(clearSelectedOrganisation());
      navigate('/');
    } catch (err: any) {
      captureSupabaseError(err, { op: 'account.delete' });
      setError(err.message || 'Failed to delete account');
      setDeletingAccount(false);
      setConfirmedAccountDeletion(false);
      setShowDeleteAccountModal(false);
    }
  };

  const openDeleteAccountModal = () => {
    setConfirmedAccountDeletion(false);
    setShowDeleteAccountModal(true);
  };

  const closeDeleteAccountModal = () => {
    if (deletingAccount) return;
    setConfirmedAccountDeletion(false);
    setShowDeleteAccountModal(false);
  };

  return (
    <Container fluid>
      <h3 className="mt-4">My Settings</h3>
      <p className="text-muted">
        Manage your account settings and preferences here.
      </p>

      {subscriptionUpdated === 'true' && (
        <div className="alert alert-success">
          {flow === 'update'
            ? 'Subscription updated successfully!'
            : 'Subscription activated successfully!'}
        </div>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={handleTabSelect}
        id="settings-tabs"
        className="mb-4"
      >
        {/* Account Tab */}
        <Tab eventKey="account" title="Account">
          <Row>
            <Col md={6} lg={4}>
              <Form>
                {error && <div className="alert alert-danger">{error}</div>}
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={firstName}
                    onChange={(e) => onChange('first_name', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={lastName}
                    onChange={(e) => onChange('last_name', e.target.value)}
                  />
                </Form.Group>
                <Button variant="primary" disabled={loading} onClick={onSave}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Form>
            </Col>
            <Col md={6} lg={4}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  readOnly
                  disabled
                  value={user?.email || ''}
                />
                <Button
                  variant="link"
                  className="p-0 mt-2"
                  onClick={() => setChangeEmail(true)}
                >
                  Change email
                </Button>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  readOnly
                  disabled
                />
                <Button
                  variant="link"
                  className="p-0 mt-2"
                  onClick={() => setChangePassword(true)}
                >
                  Change Password
                </Button>
              </Form.Group>
            </Col>
            <Col md={6} lg={4}>
              <h5>Delete Account</h5>
              <p className="text-muted small mb-3">
                Permanently delete your account and all associated assessments,
                files, and profiles.
              </p>
              <Button variant="outline-danger" onClick={openDeleteAccountModal}>
                Delete account
              </Button>
            </Col>
          </Row>
        </Tab>

        {/* Organisation Tab */}
        <Tab eventKey="organisation" title="Organisation">
          {organisations.length > 1 && (
            <p className="text-muted">
              To select organisation use the dropdown menu in the main menu.
            </p>
          )}

          {orgsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p>Loading organisations...</p>
            </div>
          ) : organisations.length > 0 ? (
            <Table responsive hover className="mt-3">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {organisations.map((org) => (
                  <tr key={org.organisation_id}>
                    <td>
                      <strong>{org.organisation_name}</strong>
                    </td>
                    <td>
                      <Badge bg="secondary">{org.role}</Badge>
                    </td>
                    <td>
                      <Badge
                        bg={org.status === 'active' ? 'success' : 'warning'}
                      >
                        {org.status}
                      </Badge>
                    </td>
                    <td>{getPlanDisplay(org)}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditOrganisation(org)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">
              You are not a member of any organisations.
            </p>
          )}
        </Tab>
      </Tabs>

      <ChangePasswordModal
        show={changePassword}
        onHide={() => setChangePassword(false)}
      />
      <ChangeEmailModal
        show={changeEmail}
        onHide={() => setChangeEmail(false)}
      />
      <SubscriptionUpgradeModal
        show={showUpgradeModal}
        onHide={() => setShowUpgradeModal(false)}
        organisation={selectedPlanUpgradeOrg}
      />
      <OrganisationEditModal
        show={showEditOrganisationModal}
        organisationId={selectedOrganisationToEdit?.organisation_id ?? null}
        organisationName={selectedOrganisationToEdit?.organisation_name}
        onHide={handleCloseEditOrganisationModal}
        onSaved={handleOrganisationSaved}
      />
      <Modal
        show={showDeleteAccountModal}
        onHide={closeDeleteAccountModal}
        centered
      >
        <Modal.Header closeButton={!deletingAccount}>
          <Modal.Title>Delete Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Are you sure you would like to delete your account and all of your
            assessments, files, and profiles? This action cannot be undone.
          </p>
          <Form.Check
            id="confirm-account-delete"
            type="checkbox"
            label="Yes, I understand that this action cannot be undone"
            checked={confirmedAccountDeletion}
            onChange={(event) =>
              setConfirmedAccountDeletion(event.target.checked)
            }
            disabled={deletingAccount}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeDeleteAccountModal}
            disabled={deletingAccount}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAccount}
            disabled={deletingAccount || !confirmedAccountDeletion}
          >
            {deletingAccount
              ? 'Deleting...'
              : 'Delete my account and all of my data'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MySettings;
